import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Form, Formik } from "formik";
import { Box, Grid, Typography } from "@mui/material";

import Field from "../../../component/Field";
import Label from "../../../component/Label";
import DataGrid from "../../../component/DataGrid";
import Toaster from "../../../component/Toaster";
import SelectField from "../../../component/SelectField";
import GlobalLoader from "../../../component/GlobalLoader";
import DateRangeCtrl from "../../../component/DateRangePicker";

import { DATE_FORMATS, DateTimeUtil } from "../../../util/DateTimeUtils";

import { paymentGatewayAction } from "../../../store/paymentGateway/paymentGatewaySlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";

import "./PaymentSettlement.scss";
import PaymentSettlementExpandedTable from "./PaymentSettlementExpandedTable";
import AdvancedSearchButtonGroup from "../../../component/AdvancedSearchButtonGroup";
import { CommonUtil } from "../../../util/Utils";
import { CommonService } from "../../../store/common/CommonSlice";

interface IPaymentSettlementProps {}

const PaymentSettlement = (props: IPaymentSettlementProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const ref = useRef(true);
  const [paymentSettlements, setpaymentSettlements] = useState<any>([]);
  const [searchedData, setSearchedData] = useState<any>(null);
  const [spinner, setSpinner] = useState<boolean>(false);
  const [isActive, setIsActive] = useState(false);
  const [state, setStates] = useState<any[]>([]);
  const [areaList, setArea] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [countryList, setCountryList] = useState<any[]>([]);

  const regionlist: any = useAppSelector((state: any) => state.region.data);
  const statelist: any = useAppSelector((state: any) => state.state.data);
  const areas: any = useAppSelector((state: any) => state.area.data);

  type initialType = {
    fromToDate: any;
    settlementStatus: string;
    country: string;

    region: string;
    state: string;
    area: string;
    subAreaName: string;

    centerCode: string;
    franchiseeName: string;
    studentName: string;
    invoiceNumber: string;
  };

  // File input change event
  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.currentTarget.value) {
  //     setSpinner(true);
  //     const fileList = e.target.files;

  //     if (!fileList) return;
  //     let formData = new FormData();
  //     formData.append("file", fileList[0]);
  //     fileUploadHandler(formData);
  //     e.currentTarget.value = "";
  //   }
  // };
  // File upload method
  // const fileUploadHandler = (fileData: any) => {
  //   dispatch(
  //     paymentGatewayAction({ apiAction: "importExcel", formValues: fileData })
  //   )
  //     .then((result: any) => {
  //       if (result?.payload?.status === 200) {
  //         getPaymentSettlements();
  //       } else {
  //         Toaster.error(undefined, result.payload);
  //       }
  //     })
  //     .catch((err: any) => {
  //       // This console for debug the app if any error occurred in DEV
  //       console.log(err);
  //     })
  //     .finally(() => {
  //       setSpinner(false);
  //     });
  // };

  const initialValue = {
    fromToDate: null,
    settlementStatus: "",
    country: countryList[0]?.value || "",
    region: "",
    state: "",
    area: "",
    subAreaName: "",

    centerCode: "",
    franchiseeName: "",
    studentName: "",
    invoiceNumber: "",
  };

  //Get region
  const getRegionList = () => {
    setRegions(CommonUtil.formateDropdownList(regionlist, "regionName"));
  };
  //Get state
  const getStateList = (regionId: any) => {
    const stateslist = statelist.filter(
      (state: any) => state.regionId === regionId
    );
    setStates(CommonUtil.formateDropdownList(stateslist, "stateName"));
  };
  //Get Area
  const getAreaList = (stateId: any) => {
    const areaslist = areas.filter((area: any) => area.stateId === stateId);
    setArea(CommonUtil.formateDropdownList(areaslist, "areaName"));
  };

  const getCountryList = () => {
    dispatch(CommonService({ apiAction: "Country" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setCountryList(
            CommonUtil.formateDropdownList(
              result?.payload?.data.data,
              "countryDisplayName"
            )
          );
        }
      })
      .catch((err: any) => {
        // This console for debug the app if any error occurred in DEV
        console.log(err);
      });
  };

  const statusOptions = [
    {
      label: "Success",
      value: "Success",
    },
    {
      label: "Sent for Processing",
      value: "Sent for Processing",
    },
  ];

  const columns = [
    {
      name: "ID",
      selector: (row: any) => row.id,
      sortable: true,
      omit: true,
    },
    {
      name: `${t("franchiseeName")}`,
      selector: (row: any) => row.franchiseeName,
      sortable: true,
      width: "12%",
    },
    {
      name: `${t("franchiseeCode")}`,
      selector: (row: any) => row.franchiseeCode,
      sortable: true,
      width: "10%",
    },
    {
      name: `${t("settlementId")}`,
      selector: (row: any) => row.settlementId,
      cell: (row: any) => row.settlementId,
      center: true,
      sortable: true,
      width: "10%",
    },
    {
      name: `${t("settlementDate")}`,
      selector: (row: any) => row.settlementInitiatedTime,
      center: true,
      sortable: true,
      width: "10%",
    },
    {
      name: `${t("settlementAmount")}`,
      selector: (row: any) => row.transactionsAmount,
      center: true,
      sortable: true,
      width: "12%",
    },
    /* This is kept for future reference */
    // {
    //   name: `${t("status")}`,
    //   selector: (row: any) => row.settlementStatus,
    //   center: true,
    //   sortable: true,
    //   width: "8%",
    // },
    {
      name: `${t("recipientName")}`,
      selector: (row: any) => row.recipientName,
      sortable: true,
      width: "10%",
    },
    {
      name: `${t("mobileNo")}`,
      selector: (row: any) => row.mobile,
      sortable: true,
      center: true,
      width: "8%",
    },
    {
      name: `${t("emailId")}`,
      selector: (row: any) => row.email,
      sortable: true,
      width: "15%",
    },
    {
      name: `${t("beneBankAcNo")}`,
      selector: (row: any) => row.beneBankAccountNumber,
      sortable: true,
      width: "12%",
    },
    {
      name: `${t("ifscCode")}`,
      selector: (row: any) => row.ifscCode,
      sortable: true,
      center: true,
      width: "8%",
    },
    {
      name: `${t("bankName")}`,
      selector: (row: any) => row.bankName,
      sortable: true,
      width: "9%",
    },
  ];

  useEffect(() => {
    if (ref.current) {
      ref.current = false;
      getPaymentSettlements();
      getRegionList();
      getCountryList();
    }
  });

  const getPaymentSettlements = () => {
    setSpinner(true);
    dispatch(paymentGatewayAction({ apiAction: "paymentGatewaySettlement" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setpaymentSettlements(result?.payload?.data.data);
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        // This console for debug the app if any error occurred in DEV
        console.log(err);
      })
      .finally(() => {
        setSpinner(false);
      });
  };

  const applyFilters = (values: initialType, filtteredSettlements: any) => {
    const searchResult = filtteredSettlements.filter(function (element: any) {
      return element.studentList.some(function (item: any) {
        return (
          (!values?.country ||
            (item?.countryName &&
              item?.countryName.toLowerCase() ===
                CommonUtil.getItemNameById(
                  values?.country,
                  countryList
                ).toLowerCase())) &&
          (!values?.region ||
            (item?.regionName &&
              item?.regionName.toLowerCase() ===
                CommonUtil.getItemNameById(
                  values?.region,
                  regions
                ).toLowerCase())) &&
          (!values?.state ||
            (item?.stateName &&
              item?.stateName.toLowerCase() ===
                CommonUtil.getItemNameById(
                  values?.state,
                  state
                ).toLowerCase())) &&
          (!values?.area ||
            (item?.areaName &&
              item?.areaName.toLowerCase() ===
                CommonUtil.getItemNameById(
                  values?.area,
                  areaList
                ).toLowerCase())) &&
          (!values?.centerCode ||
            (item?.centerCode &&
              item?.centerCode
                .toLowerCase()
                .includes(values?.centerCode?.toLowerCase()))) &&
          (!values?.franchiseeName ||
            (item?.franchiseeName &&
              item?.franchiseeName
                .toLowerCase()
                .includes(values?.franchiseeName?.toLowerCase()))) &&
          (!values?.studentName ||
            (item?.studentName &&
              item?.studentName
                .toLowerCase()
                .includes(values?.studentName?.toLowerCase()))) &&
          (!values?.invoiceNumber ||
            (item?.invoiceNumber &&
              item?.invoiceNumber
                .toLowerCase()
                .includes(values?.invoiceNumber?.toLowerCase())))
        );
      });
    });
    return searchResult;
  };

  const findSettlementsByDate = (values: any, filtteredSettlements: any) => {
    if (
      values.fromToDate &&
      values.fromToDate.length > 1 &&
      values.fromToDate[0] &&
      values.fromToDate[1]
    ) {
      const searchResult = filtteredSettlements?.filter((item: any) => {
        const fromToDate = values.fromToDate;
        let fromDate: string | Date | null = null;
        let toDate: string | Date | null = null;
        let settlementDate: string | Date | null = null;
        fromDate = DateTimeUtil.format(
          fromToDate[0].toString(),
          DATE_FORMATS.MMDDYYYY_SLASH
        );
        toDate = DateTimeUtil.format(
          fromToDate[1].toString(),
          DATE_FORMATS.MMDDYYYY_SLASH
        );
        settlementDate = DateTimeUtil.dateStringFromToFormat(
          item?.settlementInitiatedTime,
          DATE_FORMATS.DDMMYYYY_SLASH,
          DATE_FORMATS.MMDDYYYY_SLASH
        );
        fromDate = new Date(fromDate);
        toDate = new Date(toDate);
        settlementDate = new Date(settlementDate);
        return (
          fromDate &&
          toDate &&
          settlementDate &&
          settlementDate >= fromDate &&
          settlementDate <= toDate
        );
      });
      return searchResult;
    }
    return filtteredSettlements;
  };

  const findSettlementsByStatus = (values: any, filtteredSettlements: any) => {
    if (values.settlementStatus && values.settlementStatus !== "") {
      const searchResult = filtteredSettlements?.filter(
        (item: any) =>
          values?.settlementStatus &&
          item?.settlementStatus &&
          item?.settlementStatus.toLowerCase() ===
            values?.settlementStatus?.toLowerCase()
      );
      return searchResult;
    }
    return filtteredSettlements;
  };

  const handleSubmit = (values: initialType, props: any) => {
    let searchResult = findSettlementsByDate(values, paymentSettlements);

    searchResult = findSettlementsByStatus(values, searchResult);

    searchResult = applyFilters(values, searchResult);

    setSearchedData(searchResult);
  };

  const onClickClear = () => {
    setSearchedData(false);
    setStates([]);
    setArea([]);
  };

  // const onClickExport = () => {
  //   setSpinner(true);
  //   dispatch(
  //     paymentGatewayAction({
  //       apiAction: "exportExcel",
  //     })
  //   )
  //     .then((result: any) => {
  //       if (result?.payload?.status === 200) {
  //         CommonUtil.download(result?.payload);
  //       } else {
  //         Toaster.error(undefined, result.payload);
  //       }
  //     })
  //     .catch((err: any) => {
  //       //This console for debug the app if any error occurred in DEV
  //       console.log(err);
  //     })
  //     .finally(() => {
  //       setSpinner(false);
  //     });
  // };

  return (
    <Box className="module-container">
      <Box className="flex justify-content-between">
        <div>
          <Typography className="sub-module-header popins">
            {t("payments")}
          </Typography>
        </div>

        {/* This is kept for future reference */}
        {/* <div className="flex">
          <div className="padding-x-10">
            <ActionButton
              label={t("export")}
              actionButtonStyle="dark"
              onClick={onClickExport}
            />
          </div>
          <div className="padding-x-10">
            <ActionButton
              label={t("import")}
              type="button"
              upload={true}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              actionButtonStyle="dark"
              onChange={handleFileChange}
            />
          </div>
        </div> */}
      </Box>

      <Formik
        initialValues={initialValue}
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          errors,
          touched,
          setFieldValue,
          resetForm,
        }: any) => {
          return (
            <Form>
              <Box
                sx={{ width: "100%", padding: "20px 15px", marginTop: "20px" }}
                className="blue-border"
              >
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={{ xs: 1, sm: 2, md: 3, lg: 4 }}
                >
                  <Grid item xs={12} sm={12} md={3} lg={3}>
                    <div>
                      <Label title={t("fromToDate")} />
                      <div className="date-picker doubleDatePicker">
                        <DateRangeCtrl
                          name="fromToDate"
                          onChange={(value: any) => {
                            setFieldValue("fromToDate", value);
                          }}
                          value={values.fromToDate}
                        />
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3}>
                    <div>
                      <Label title={t("settlementStatus")} />
                      <SelectField
                        name="settlementStatus"
                        value={values.settlementStatus || t("select")}
                        onChange={handleChange}
                        options={statusOptions}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3}>
                    <div>
                      <Label title={t("country")} />
                      <SelectField
                        name="country"
                        value={values.country || t("select")}
                        onChange={(e: any) => {
                          setTimeout(() => {
                            setFieldValue("country", e.target.value);
                          });
                        }}
                        options={countryList}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3}>
                    {!isActive && (
                      <AdvancedSearchButtonGroup
                        expandAdvanceSearch={() => setIsActive(!isActive)}
                        clearSearch={() => {
                          resetForm();
                          onClickClear();
                        }}
                        isActive={isActive}
                      />
                    )}
                  </Grid>
                </Grid>
                {isActive && (
                  <Grid
                    container
                    rowSpacing={1}
                    columnSpacing={{ xs: 1, sm: 2, md: 3, lg: 4 }}
                  >
                    <Grid item xs={12} sm={12} md={3} lg={3}>
                      <div>
                        <Label title={t("franchiseeName")} />
                        <Field
                          name="franchiseeName"
                          placeholder={t("enterHere")}
                          value={values.franchiseeName}
                          enterkey="true"
                          onChange={handleChange}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}>
                      <div>
                        <Label title={t("region")} />
                        <SelectField
                          name="region"
                          value={values.region || t("select")}
                          onChange={(e: any) => {
                            setArea([]);
                            setTimeout(() => {
                              setFieldValue("region", e.target.value);
                            });
                            getStateList(e.target.value);
                          }}
                          options={regions}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}>
                      <div>
                        <Label title={t("state")} />
                        <SelectField
                          name="state"
                          value={values.state || t("select")}
                          onChange={(e: any) => {
                            setArea([]);
                            setTimeout(() => {
                              setFieldValue("state", e.target.value);
                            });
                            getAreaList(e.target.value);
                          }}
                          options={state}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}></Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}>
                      <div>
                        <Label title={t("area")} />
                        <SelectField
                          name="area"
                          value={values.area || t("select")}
                          onChange={(e: any) => {
                            setFieldValue("area", e.target.value);
                          }}
                          options={areaList}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}>
                      <div>
                        <Label title={t("franchiseeCode")} />
                        <Field
                          name="centerCode"
                          placeholder={t("enterHere")}
                          value={values.centerCode}
                          enterkey="true"
                          onChange={handleChange}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                      <div>
                        <Label title={t("studentName")} />
                        <Field
                          name="studentName"
                          placeholder={t("enterHere")}
                          value={values.studentName}
                          enterkey="true"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.studentName && touched.studentName}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}></Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}>
                      <div>
                        <Label title={t("invoiceNo")} />
                        <Field
                          name="invoiceNumber"
                          placeholder={t("enterHere")}
                          value={values.invoiceNumber}
                          enterkey="true"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.invoiceNumber && touched.invoiceNumber}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}></Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}></Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3}>
                      <AdvancedSearchButtonGroup
                        expandAdvanceSearch={() => setIsActive(!isActive)}
                        clearSearch={() => {
                          resetForm();
                          onClickClear();
                        }}
                        isActive={isActive}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Form>
          );
        }}
      </Formik>
      <Box className="headingSpacing">
        <Label title={t("settlementList")} />
        <Box>
          <DataGrid
            columns={columns}
            data={searchedData ? searchedData : paymentSettlements}
            action={paymentGatewayAction}
            selectableRows={false}
            selectableRowsHighlight={true}
            highlightOnHover={true}
            canMultipleDelete={false}
            canExport={false}
            defaultSortFieldId={1}
            expandableRows
            expandableRowsComponent={PaymentSettlementExpandedTable}
          />
        </Box>
      </Box>
      <GlobalLoader loading={spinner} />
    </Box>
  );
};

export default PaymentSettlement;
